/**
 * Seat a bone that a sibling skin already binds into THIS mesh's skeleton —
 * by bone OBJECT and with the sibling's inverse bind, so the two skins agree
 * on the bone's frame exactly.
 *
 * Why not the hands' seat (bindGraftedDigits): it resolves bones by scene
 * NAME, which is right for grafted helpers (born by name, unique) and wrong
 * here — a multi-skeleton target's foreign skin carries the very same names
 * (ADR-029, Codex P2 #108), and the bone to seat is the selected group's
 * object, not whichever node answers first.
 *
 * The case (Codex P2, #108): a garment's subset Skeleton never referenced
 * the pelvis owner (a skirt weighted only to Waist), yet it holds donor
 * paint below the station — legitimate under the supported subset-skeleton
 * layout, not a rig contradiction. Seating the owner first lets the re-own
 * move that paint where the body's goes. Idempotent: a bone already seated
 * is left alone. A fresh Skeleton rather than pushing in place, so three
 * re-sizes boneMatrices/boneTexture (the hands' law).
 *
 * Depends on: three. Depended on by: the Document driver's pelvis pass.
 */
import { Skeleton, Uint16BufferAttribute, type Bone, type SkinnedMesh } from 'three';

export class SeatSharedBoneError extends Error {}

/**
 * The skin to seat FROM: one that actually binds the bone — never "the
 * largest" (Codex P2, #108): under overlap grouping a garment with enough
 * private helpers can out-size the body while omitting the pelvis owner,
 * and seating from it is impossible. Null when no skin in the group binds
 * the bone (the door then decides: donors present is a contradiction,
 * none is a foreign-skin skip).
 */
export function seatSourceFor(meshes: readonly SkinnedMesh[], boneName: string): SkinnedMesh | null {
  return meshes.find((m) => m.skeleton.bones.some((b) => b.name === boneName)) ?? null;
}

export function seatSharedBone(mesh: SkinnedMesh, sibling: SkinnedMesh, boneName: string): { seated: boolean } {
  if (mesh.skeleton.bones.some((b) => b.name === boneName)) return { seated: false };
  const index = sibling.skeleton.bones.findIndex((b) => b.name === boneName);
  if (index < 0) {
    throw new SeatSharedBoneError(`sibling skin "${sibling.name}" does not bind "${boneName}" — nothing to seat from`);
  }
  const bone = sibling.skeleton.bones[index] as Bone;
  const inverse = sibling.skeleton.boneInverses[index]!.clone();
  // The new slot can exceed what a Uint8 skinIndex addresses; widen rather
  // than let a later write wrap to slot 0 — the hands' seat's law (Codex P2, #108).
  const newSlot = mesh.skeleton.bones.length;
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  if (skinIndex && newSlot > 255 && (skinIndex.array as ArrayLike<number>) instanceof Uint8Array) {
    const widened = new Uint16Array(skinIndex.count * skinIndex.itemSize);
    for (let i = 0; i < widened.length; i += 1) widened[i] = (skinIndex.array as ArrayLike<number>)[i]!;
    mesh.geometry.setAttribute('skinIndex', new Uint16BufferAttribute(widened, skinIndex.itemSize));
  }
  mesh.bind(new Skeleton([...mesh.skeleton.bones, bone], [...mesh.skeleton.boneInverses, inverse]), mesh.bindMatrix.clone());
  return { seated: true };
}
